<template>
    <section class="quick-question-editor" :aria-label="$t('AssistantPanel.QuickQuestionsEditorTitle')">
        <header class="editor-head">
            <div class="editor-title-block">
                <strong>
                    {{ $t('AssistantPanel.QuickQuestionsEditorTitle') }} · {{ contextLabel }}
                </strong>
                <span>{{ $t('AssistantPanel.QuickQuestionsEditorDescription') }}</span>
            </div>
            <button type="button" class="editor-close"
                    :aria-label="$t('Close')" :title="$t('Close')"
                    @click="$emit('close')">×</button>
        </header>

        <div class="editor-body">
            <div class="question-list">
                <div v-for="(question, index) in draftQuestions" :key="index"
                     class="question-row">
                    <span class="question-order" aria-hidden="true">{{ index + 1 }}</span>
                    <textarea v-model="draftQuestions[index]"
                              rows="2"
                              :maxlength="maxLength"
                              :aria-label="$t('AssistantPanel.QuickQuestionNumber', { number: index + 1 })"></textarea>
                    <div class="question-actions">
                        <button type="button" :disabled="index === 0"
                                :title="$t('AssistantPanel.MoveQuestionUp')"
                                :aria-label="$t('AssistantPanel.MoveQuestionUp')"
                                @click="move(index, -1)">↑</button>
                        <button type="button" :disabled="index === draftQuestions.length - 1"
                                :title="$t('AssistantPanel.MoveQuestionDown')"
                                :aria-label="$t('AssistantPanel.MoveQuestionDown')"
                                @click="move(index, 1)">↓</button>
                        <button type="button"
                                :title="$t('AssistantPanel.RemoveQuestion')"
                                :aria-label="$t('AssistantPanel.RemoveQuestion')"
                                @click="remove(index)">×</button>
                    </div>
                </div>
            </div>

            <button type="button" class="add-question"
                    :disabled="draftQuestions.length >= maxCount"
                    @click="add">＋ {{ $t('AssistantPanel.AddQuickQuestion') }}</button>
            <p class="editor-hint">
                {{ $t('AssistantPanel.QuickQuestionsLimits', { count: maxCount, length: maxLength }) }}
            </p>
            <p v-if="errorMessage" class="editor-error" role="alert">{{ errorMessage }}</p>
        </div>

        <footer class="editor-footer">
            <div v-if="resetConfirmOpen" class="reset-confirm" role="alert">
                <span>{{ $t('AssistantPanel.ResetQuickQuestionsConfirm') }}</span>
                <div>
                    <button type="button" class="editor-btn editor-btn--ghost"
                            :disabled="saving" @click="resetConfirmOpen = false">{{ $t('Cancel') }}</button>
                    <button type="button" class="editor-btn editor-btn--warning"
                            :disabled="saving" @click="resetQuestions">{{ $t('Confirm') }}</button>
                </div>
            </div>
            <template v-else>
                <button type="button" class="editor-btn editor-btn--reset"
                        :disabled="saving || !isCustomized"
                        @click="resetConfirmOpen = true">
                    {{ $t('AssistantPanel.ResetQuickQuestions') }}
                </button>
                <div class="editor-actions">
                    <button type="button" class="editor-btn editor-btn--ghost"
                            :disabled="saving" @click="$emit('close')">{{ $t('Cancel') }}</button>
                    <button type="button" class="editor-btn editor-btn--primary"
                            :disabled="saving" @click="save">
                        {{ saving ? $t('Saving') : $t('Save') }}
                    </button>
                </div>
            </template>
        </footer>
    </section>
</template>

<script>
    import AiAssistantQuickQuestionService from '../../services/aiAssistantQuickQuestion';

    export default {
        name: 'AssistantQuickQuestionsEditor',
        props: {
            scope: { type: String, required: true },
            contextLabel: { type: String, required: true },
            questions: { type: Array, required: true },
            isCustomized: { type: Boolean, default: false },
            maxCount: { type: Number, default: 6 },
            maxLength: { type: Number, default: 160 }
        },
        emits: ['close', 'saved', 'reset'],
        data() {
            return {
                draftQuestions: this.questions.slice(),
                saving: false,
                errorMessage: '',
                resetConfirmOpen: false
            };
        },
        methods: {
            add() {
                if (this.draftQuestions.length >= this.maxCount) return;
                this.draftQuestions.push('');
                this.$nextTick(() => {
                    const fields = this.$el.querySelectorAll('.question-row textarea');
                    const field = fields[fields.length - 1];
                    if (field) field.focus();
                });
            },
            remove(index) {
                this.draftQuestions.splice(index, 1);
            },
            move(index, offset) {
                const target = index + offset;
                if (target < 0 || target >= this.draftQuestions.length) return;
                const reordered = this.draftQuestions.slice();
                [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
                this.draftQuestions = reordered;
            },
            validate() {
                const normalized = this.draftQuestions.map(question => String(question || '').trim());
                if (!normalized.length || normalized.length > this.maxCount) {
                    return { error: this.$t('AssistantPanel.QuickQuestionsCountError', { count: this.maxCount }) };
                }
                if (normalized.some(question => question.length < 2 || question.length > this.maxLength)) {
                    return { error: this.$t('AssistantPanel.QuickQuestionsLengthError', { length: this.maxLength }) };
                }
                const unique = new Set(normalized.map(question => question.toLocaleLowerCase('tr-TR')));
                if (unique.size !== normalized.length) {
                    return { error: this.$t('AssistantPanel.QuickQuestionsDuplicateError') };
                }
                return { questions: normalized };
            },
            async save() {
                const validation = this.validate();
                if (validation.error) {
                    this.errorMessage = validation.error;
                    return;
                }

                this.saving = true;
                this.errorMessage = '';
                try {
                    const result = await AiAssistantQuickQuestionService.save(this.scope, validation.questions);
                    this.$emit('saved', result);
                } catch (error) {
                    this.errorMessage = error && error.response && error.response.status === 409
                        ? this.$t('AssistantPanel.QuickQuestionsConflictError')
                        : this.$t('AssistantPanel.QuickQuestionsSaveError');
                } finally {
                    this.saving = false;
                }
            },
            async resetQuestions() {
                this.saving = true;
                this.errorMessage = '';
                try {
                    const result = await AiAssistantQuickQuestionService.reset(this.scope);
                    this.$emit('reset', result);
                } catch {
                    this.errorMessage = this.$t('AssistantPanel.QuickQuestionsResetError');
                    this.resetConfirmOpen = false;
                } finally {
                    this.saving = false;
                }
            }
        }
    };
</script>

<style scoped>
    .quick-question-editor {
        position: absolute;
        inset: 0;
        z-index: 6;
        display: flex;
        flex-direction: column;
        min-height: 0;
        background: #fff;
        color: #1e293b;
    }
    .editor-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 13px;
        padding: 13px 14px;
        border-bottom: 1px solid #f1f5f9;
        flex-shrink: 0;
    }
    .editor-title-block {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
    }
    .editor-title-block strong {
        font-size: var(--est-fs-body);
        font-weight: 600;
        color: #1e1b34;
    }
    .editor-title-block span {
        color: #64748b;
        font-size: var(--est-fs-meta);
        line-height: 1.4;
    }
    .editor-close {
        width: 26px;
        height: 26px;
        flex: 0 0 26px;
        border: none;
        border-radius: 6px;
        background: transparent;
        color: #94a3b8;
        font-size: 17px;
        cursor: pointer;
    }
    .editor-close:hover { background: #f1f5f9; color: #475569; }
    .editor-body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 12px 14px;
    }
    .question-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .question-row {
        display: grid;
        grid-template-columns: 22px minmax(0, 1fr) 72px;
        align-items: center;
        gap: 6px;
    }
    .question-order {
        color: #94a3b8;
        font-size: var(--est-fs-badge);
        text-align: center;
    }
    .question-row textarea {
        width: 100%;
        min-height: 52px;
        resize: vertical;
        padding: 8px 10px;
        border: 1px solid #dce2ea;
        border-radius: 8px;
        background: #fff;
        color: #334155;
        font-size: var(--est-fs-meta);
        line-height: 1.35;
    }
    .question-row textarea:focus {
        border-color: #dce2ea;
        outline: 3px solid rgba(23, 26, 31, .07);
    }
    .question-actions {
        display: flex;
        align-items: center;
        gap: 2px;
    }
    .question-actions button {
        width: 22px;
        height: 30px;
        padding: 0;
        border: none;
        border-radius: 7px;
        background: transparent;
        color: #64748b;
        cursor: pointer;
    }
    .question-actions button:hover:not(:disabled) { background: #f1f5f9; color: #4338ca; }
    .question-actions button:disabled { opacity: .3; cursor: default; }
    .add-question {
        width: 100%;
        min-height: 34px;
        margin-top: 10px;
        border: 1px dashed #cbd5e1;
        border-radius: 8px;
        background: transparent;
        color: #6d4bd1;
        font-size: var(--est-fs-meta);
        cursor: pointer;
    }
    .add-question:hover:not(:disabled) { background: #f8f7ff; border-color: #9b87dd; }
    .add-question:disabled { opacity: .45; cursor: default; }
    .editor-hint, .editor-error {
        margin: 7px 0 0;
        font-size: var(--est-fs-meta);
        line-height: 1.4;
    }
    .editor-hint { color: #94a3b8; }
    .editor-error { color: #b42318; }
    .editor-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 10px 14px;
        border-top: 1px solid #f1f5f9;
        flex-shrink: 0;
    }
    .editor-actions, .reset-confirm, .reset-confirm > div {
        display: flex;
        align-items: center;
        gap: 6px;
    }
    .reset-confirm {
        width: 100%;
        justify-content: space-between;
        color: #9a5c00;
        font-size: var(--est-fs-meta);
        line-height: 1.35;
    }
    .editor-btn {
        min-height: 34px;
        padding: 0 11px;
        border-radius: 8px;
        font-size: var(--est-fs-meta);
        cursor: pointer;
    }
    .editor-btn:disabled { opacity: .45; cursor: default; }
    .editor-btn--ghost, .editor-btn--reset {
        border: 1px solid #dce2ea;
        background: #fff;
        color: #64748b;
    }
    .editor-btn--reset { color: #9a5c00; }
    .editor-btn--warning {
        border: 1px solid #b26a00;
        background: #fff;
        color: #9a5c00;
    }
    .editor-btn--primary {
        border: 1px solid #7c3aed;
        background: #7c3aed;
        color: #fff;
    }
    @media (max-width: 420px) {
        .question-row { grid-template-columns: 20px minmax(0, 1fr); }
        .question-actions { grid-column: 2; justify-content: flex-end; }
        .editor-footer { align-items: stretch; flex-direction: column; }
        .editor-actions { align-self: flex-end; }
        .reset-confirm { align-items: flex-start; flex-direction: column; }
        .reset-confirm > div { align-self: flex-end; }
    }
</style>
